import React, { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function formatDateTime(value) {
  if (!value) return 'Not sent';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function statusClasses(status) {
  if (status === 'sent') return 'bg-green-100 text-green-800';
  if (status === 'recorded') return 'bg-blue-100 text-blue-800';
  if (status === 'failed') return 'bg-red-100 text-red-800';
  if (status === 'voided') return 'bg-gray-200 text-gray-700';
  return 'bg-amber-100 text-amber-800';
}

function requestId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function CommunicationHistory({ caseId, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [channel, setChannel] = useState('all');
  const [openItems, setOpenItems] = useState({});
  const [resending, setResending] = useState({});
  const resendLocks = useRef(new Set());

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError('');
    try {
      const query = channel === 'all' ? '' : `?channel=${encodeURIComponent(channel)}`;
      const response = await fetch(`${API_BASE}/case/${caseId}/communications/${query}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Could not load communications.');
      setItems(payload.communications || []);
    } catch (requestError) {
      setError(requestError.message || 'Could not load communications.');
    } finally {
      setLoading(false);
    }
  }, [caseId, channel, refreshKey]);

  useEffect(() => {
    load();
  }, [load]);

  async function resend(item) {
    if (resendLocks.current.has(item.id)) return;
    resendLocks.current.add(item.id);
    setResending((current) => ({ ...current, [item.id]: true }));
    setError('');
    try {
      const response = await fetch(`${API_BASE}/communications/${item.id}/resend/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ request_id: requestId() }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'The email could not be resent.');
      await load();
    } catch (requestError) {
      setError(requestError.message || 'The email could not be resent.');
    } finally {
      resendLocks.current.delete(item.id);
      setResending((current) => ({ ...current, [item.id]: false }));
    }
  }

  async function downloadAttachment(attachment) {
    setError('');
    try {
      const response = await fetch(`${API_BASE}${attachment.download_url}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (!response.ok) throw new Error('The attachment could not be downloaded.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.name || 'attachment';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.message || 'The attachment could not be downloaded.');
    }
  }

  return (
    <section className="mt-8 rounded-lg bg-white p-6 shadow">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Communications History</h2>
          <p className="mt-1 text-sm text-gray-600">
            Automated emails, composed emails, and staff-confirmed text messages for this case.
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={channel}
            onChange={(event) => setChannel(event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="all">Email and text</option>
            <option value="email">Email only</option>
            <option value="sms">Text only</option>
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mt-5 space-y-3">
        {!loading && items.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-5 text-sm text-gray-600">
            No communications have been recorded for this case yet.
          </div>
        )}
        {items.map((item) => {
          const open = Boolean(openItems[item.id]);
          return (
            <div key={item.id} className="overflow-hidden rounded-lg border border-gray-200">
              <button
                type="button"
                onClick={() => setOpenItems((current) => ({ ...current, [item.id]: !open }))}
                className="flex w-full items-start justify-between gap-4 bg-white px-4 py-4 text-left hover:bg-gray-50"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      {item.subject || item.communication_type.split('_').join(' ')}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusClasses(item.status)}`}>
                      {item.status_display}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {item.channel_display} · {formatDateTime(item.sent_at || item.created_at)} · {item.sender_name || 'Firm'}
                  </div>
                  <div className="mt-1 truncate text-sm text-gray-600">
                    To: {(item.recipients || []).join(', ') || 'No recipient recorded'}
                  </div>
                </div>
                <span className={`mt-1 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}>⌄</span>
              </button>

              {open && (
                <div className="border-t border-gray-200 bg-gray-50 px-4 py-4">
                  <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <div><dt className="font-semibold text-gray-700">Type</dt><dd className="mt-1 capitalize text-gray-600">{item.communication_type.split('_').join(' ')}</dd></div>
                    <div><dt className="font-semibold text-gray-700">Source</dt><dd className="mt-1 text-gray-600">{item.source_display}</dd></div>
                    <div><dt className="font-semibold text-gray-700">Sent by</dt><dd className="mt-1 text-gray-600">{item.employee?.name || item.sender_name || 'Automated system'}</dd></div>
                    <div><dt className="font-semibold text-gray-700">To</dt><dd className="mt-1 break-all text-gray-600">{(item.recipients || []).join(', ') || '—'}</dd></div>
                    <div><dt className="font-semibold text-gray-700">CC</dt><dd className="mt-1 break-all text-gray-600">{(item.cc || []).join(', ') || '—'}</dd></div>
                    {item.provider_message_id && <div><dt className="font-semibold text-gray-700">Provider reference</dt><dd className="mt-1 break-all text-gray-600">{item.provider_message_id}</dd></div>}
                  </dl>
                  {item.body_text && (
                    <div className="mt-4">
                      <div className="text-sm font-semibold text-gray-700">Message</div>
                      <div className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-white p-3 text-sm text-gray-700">
                        {item.body_text}
                      </div>
                    </div>
                  )}
                  {item.error_message && (
                    <pre className="mt-4 overflow-auto whitespace-pre-wrap rounded-md bg-red-50 p-3 text-xs text-red-800">
                      {item.error_message}
                    </pre>
                  )}
                  {(item.attachments || []).length > 0 && (
                    <div className="mt-4">
                      <div className="text-sm font-semibold text-gray-700">Attachments</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {item.attachments.map((attachment) => (
                          <button
                            key={attachment.id}
                            type="button"
                            onClick={() => downloadAttachment(attachment)}
                            className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
                          >
                            Download {attachment.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    {item.can_resend && (
                      <button
                        type="button"
                        onClick={() => resend(item)}
                        disabled={resending[item.id]}
                        className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {resending[item.id] ? 'Resending…' : 'Resend email'}
                      </button>
                    )}
                    {item.resend_count > 0 && (
                      <span className="text-xs text-gray-500">Resent {item.resend_count} time{item.resend_count === 1 ? '' : 's'}</span>
                    )}
                    {item.resend_of && <span className="text-xs text-gray-500">This is a resend.</span>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
